import { Link, useParams } from "react-router-dom";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import Faq from "@/components/Faq";
import VideoEmbed from "@/components/VideoEmbed";
import AdamQuestionnaire from "@/components/AdamQuestionnaire";
import { Reveal, Label } from "@/components/ui-bits";
import { ARTICLES } from "@/data/articles";
import { SERVICES } from "@/data/services";
import AskAndrogen from "@/components/AskAndrogen";
import NotFound from "./NotFound";

function ArticleSection({ sec }) {
  return (
    <article>
      <h2 className="font-display font-semibold text-3xl sm:text-4xl tracking-wide">{sec.h2}</h2>
      {sec.paras?.map((p, j) => (
        <p key={j} className="text-mute text-base leading-relaxed mt-5">{p}</p>
      ))}
      {sec.bullets && (
        <ul className="mt-6 space-y-3">
          {sec.bullets.map((b) => (
            <li key={b} className="flex gap-3 text-bone/85 text-sm sm:text-base leading-relaxed">
              <span className="text-teal mt-1">·</span> {b}
            </li>
          ))}
        </ul>
      )}
      {sec.table && <ArticleTable rows={sec.table} />}
    </article>
  );
}

function ArticleTable({ rows }) {
  return (
    <div className="mt-7 overflow-x-auto card-line rounded-2xl" data-testid="article-comparison-table">
      <table className="w-full text-sm">
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri} className={ri < rows.length - 1 ? "border-b border-line" : ""}>
              {row.map((cell, ci) => (
                <td
                  key={ci}
                  className={`px-5 py-3.5 align-top ${ci === 0 ? "font-mono text-dusk text-xs uppercase tracking-wider w-36" : "text-bone/85"}`}
                >
                  {ri === 0 && ci > 0 ? <span className="font-display text-lg tracking-wide">{cell}</span> : cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RelatedCard({ slug }) {
  const article = ARTICLES.find((x) => x.slug === slug);
  const service = SERVICES[slug];
  if (!article && !service) return null;
  const to = article ? `/learn/${slug}` : `/${slug}`;
  const label = article ? article.label : `Service — ${service.label}`;
  const title = article ? article.title : service.title;
  const foot = article ? article.reading : "Service page";
  return (
    <Reveal className="h-full">
      <Link to={to} className="card-line rounded-2xl p-7 block h-full hover:border-teal/30 transition-colors" data-testid={`article-related-${slug}`}>
        <p className="mono-label text-dusk">{label}</p>
        <h3 className="font-display text-2xl tracking-wide mt-3">{title}</h3>
        <p className="mono-label text-teal mt-4">{foot}</p>
      </Link>
    </Reveal>
  );
}

export default function ArticlePage({ slug: fixedSlug }) {
  const params = useParams();
  const slug = fixedSlug || params.slug;
  const a = ARTICLES.find((x) => x.slug === slug);
  if (!a) return <NotFound />;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "MedicalWebPage",
    headline: a.title,
    description: a.lede,
    inLanguage: "en-GB",
    image: a.image,
    dateModified: a.updated,
    author: { "@type": "Organization", name: "Androgen Health clinical team" },
    publisher: { "@type": "Organization", name: "Androgen Health" },
  };

  return (
    <>
      <Seo title={`${a.title} | Androgen Health`} description={a.lede.slice(0, 158)} jsonLd={jsonLd} image={a.image} imageAlt={a.imageAlt} />
      <PageHero
        label={`Knowledge centre — ${a.label}`}
        title={a.title}
        lede={a.lede}
        safety={false}
        meta={[a.reading, `Review date ${a.updated}`, a.author].map((m) => (
          <span key={m} className="px-4 py-2 rounded-full border border-line mono-label text-dusk">{m}</span>
        ))}
      />

      <section className="pb-20 px-5 sm:px-8" data-testid="article-body">
        <div className="max-w-7xl mx-auto">
          {a.image && (
            <Reveal>
              <figure className="relative rounded-3xl overflow-hidden card-line mb-12 aspect-[21/9]" data-testid="article-hero-image">
                <img src={a.image} alt={a.imageAlt || ""} className="absolute inset-0 w-full h-full object-cover" loading="eager" fetchPriority="high" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/20 to-transparent" />
                <figcaption className="absolute left-6 bottom-5 mono-label text-bone/70">{a.imageAlt}</figcaption>
              </figure>
            </Reveal>
          )}

          <Reveal>
            <div className="card-line rounded-2xl p-8 sm:p-10 border-l-2 !border-l-teal" data-testid="article-direct-answer">
              <Label>The direct answer</Label>
              <p className="text-bone/90 text-base sm:text-lg leading-relaxed mt-4">{a.answer}</p>
            </div>
          </Reveal>

          {a.widget === "adam" && (
            <div className="mt-12 max-w-3xl" data-testid="article-widget-adam">
              <Reveal>
                <AdamQuestionnaire />
              </Reveal>
            </div>
          )}

          {a.video && (
            <div className="mt-12 max-w-3xl">
              <Reveal>
                <VideoEmbed video={a.video} poster={a.image} />
              </Reveal>
            </div>
          )}

          <div className="mt-16 space-y-12 max-w-3xl">
            {a.body.map((sec, i) => (
              <Reveal key={sec.h2} delay={i * 0.03}>
                <ArticleSection sec={sec} />
              </Reveal>
            ))}
          </div>

          <div className="mt-20 max-w-3xl">
            <Reveal>
              <Label>Related questions</Label>
            </Reveal>
            <div className="mt-6">
              <Faq items={a.faqs} />
            </div>
            <p className="text-xs text-dusk mt-10 leading-relaxed">
              Written by the Androgen Health clinical team · Review date {a.updated} · Due clinical
              and regulatory review before launch · For information only, not medical advice.
            </p>
          </div>

          <div className="mt-16">
            <Reveal>
              <Label>Continue reading</Label>
            </Reveal>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-6">
              {a.related.map((rslug) => (
                <RelatedCard key={rslug} slug={rslug} />
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="pb-24 px-5 sm:px-8" data-testid="article-ask-section">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <Label className="mb-8">Still have a question?</Label>
          </Reveal>
          <AskAndrogen />
        </div>
      </section>
    </>
  );
}
